import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Navigation from './components/Navigation';
import HomePage from './pages/HomePage';
import BoardPage from './pages/BoardPage';
import ProfilePage from './pages/ProfilePage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';
import MenuManagement from './pages/MenuManagement';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import TravelPlanPage from './pages/TravelPlanPage';
import TravelDetailPage from './pages/TravelDetailPage';
import SharePreviewPage from './pages/SharePreviewPage';
import { ServerConfigProvider, useServerConfig } from './context/ServerConfigContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { JWTAuthProvider, useJWTAuth } from './context/JWTAuthContext';
import { hasMenuPermission, getUserGradeString } from './utils/menuPermissions';

// 메뉴 권한 기반 라우트 보호 컴포넌트
function ProtectedRoute({ children, menuId }) {
  const { user, isAuthenticated } = useJWTAuth();
  const { config, isConfigLoaded } = useServerConfig();

  // 설정이 로드되지 않았으면 로딩 표시
  if (!isConfigLoaded) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>권한을 확인하는 중...</p>
      </div>
    );
  }

  // 현재 사용자의 등급 확인 (비로그인 시 guest)
  const currentUserGrade = isAuthenticated ? user?.grade : null;

  // 메뉴 접근 권한 확인
  const hasPermission = hasMenuPermission(currentUserGrade, menuId, config, user);

  console.log(`🔐 [ProtectedRoute] 권한 확인: ${getUserGradeString(currentUserGrade)} -> ${menuId} = ${hasPermission}`);

  if (!hasPermission) {
    // 권한이 없으면 404처럼 처리 (여행 플래너 페이지로 리다이렉트)
    return <Navigate to="/travel" replace />;
  }

  return children;
}

// 관리자 전용 라우트 보호 컴포넌트
function AdminRoute({ children }) {
  console.log('===== AdminRoute 실행됨 =====');
  const { user, isAuthenticated } = useJWTAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'moderator' ||
                 // 임시: 개발 모드에서 로그인된 사용자는 관리자로 간주
                 (process.env.NODE_ENV === 'development' && isAuthenticated);

  // 디버깅 로그 추가
  console.log('🔐 [AdminRoute] 권한 확인:', {
    isAuthenticated,
    user: user,
    userRole: user?.role,
    isAdmin,
    NODE_ENV: process.env.NODE_ENV
  });

  // alert로도 확인 (임시 - 나중에 제거)
  // if (typeof user !== 'undefined') {
  //   alert(`AdminRoute 체크:
  // 인증상태: ${isAuthenticated}
  // 사용자: ${JSON.stringify(user)}
  // 역할: ${user?.role}
  // 관리자여부: ${isAdmin}
  // 환경: ${process.env.NODE_ENV}`);
  // }

  // production 모드에서는 관리자 페이지 접근 차단
  if (process.env.NODE_ENV === 'production') {
    console.log('🚫 [AdminRoute] production 모드에서 관리자 페이지 차단');
    return <Navigate to="/board" replace />;
  }

  if (!isAuthenticated || !isAdmin) {
    console.log('🚫 [AdminRoute] 권한 없음 - 리다이렉트', {
      isAuthenticated,
      isAdmin,
      userRole: user?.role
    });
    // 권한이 없으면 board 페이지로 리다이렉트 (404처럼 처리)
    return <Navigate to="/board" replace />;
  }

  console.log('✅ [AdminRoute] 관리자 권한 승인');
  return children;
}

// 메인 앱 컴포넌트
function MainApp() {
  console.log('🚀 MainApp 렌더링됨!');
  const { user, isAuthenticated, isLoading } = useJWTAuth();

  console.log('👤 사용자 상태:', { user, isAuthenticated, isLoading });

  // 관리자 권한 체크
  const isAdmin = user?.role === 'admin' || user?.role === 'moderator';

  // 인증 로딩 중
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="loading-spinner"></div>
        <p>앱을 준비하는 중...</p>
      </div>
    );
  }

  return (
      <div className="app">
        <Navigation showSettings={isAuthenticated && isAdmin} />
        <Routes>
          {/* 홈 페이지 - 여행 플래너로 리다이렉트 */}
          <Route path="/" element={<Navigate to="/travel" replace />} />
          <Route path="/home" element={<HomePage />} />

          {/* 게시판 - 권한 보호 */}
          <Route path="/board" element={
            <ProtectedRoute menuId="board">
              <BoardPage />
            </ProtectedRoute>
          } />
          <Route path="/board/:boardId" element={
            <ProtectedRoute menuId="board">
              <BoardPage />
            </ProtectedRoute>
          } />
          <Route path="/board/:boardId/post/:postId" element={
            <ProtectedRoute menuId="board">
              <BoardPage />
            </ProtectedRoute>
          } />
          <Route path="/board/:boardId/write" element={
            <ProtectedRoute menuId="board">
              <BoardPage />
            </ProtectedRoute>
          } />

          {/* 여행 플래너 - 권한 보호 */}
          <Route path="/travel" element={
            <ProtectedRoute menuId="travel">
              <TravelPlanPage />
            </ProtectedRoute>
          } />
          <Route path="/travel/:planId" element={
            <ProtectedRoute menuId="travel">
              <TravelDetailPage />
            </ProtectedRoute>
          } />

          {/* 공유 링크 미리보기 - 인증 불필요 */}
          <Route path="/share/:token" element={<SharePreviewPage />} />

          {/* 사용자 페이지 */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />

          {/* 관리자 페이지 */}
          <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
          <Route path="/menu-management" element={<AdminRoute><MenuManagement /></AdminRoute>} />
          <Route path="/settings" element={<AdminRoute><SettingsPage /></AdminRoute>} />

          {/* 404 페이지 - 여행 플래너로 리다이렉트 */}
          <Route path="*" element={<Navigate to="/travel" replace />} />
        </Routes>
      </div>
  );
}

// 최상위 App 컴포넌트
function App() {
  console.log('🎯 App 컴포넌트 시작!');
  return (
    <Router>
      <ServerConfigProvider>
        <AuthProvider>
          <JWTAuthProvider>
            <MainApp />
          </JWTAuthProvider>
        </AuthProvider>
      </ServerConfigProvider>
    </Router>
  );
}

export default App;